import React, { useRef } from 'react';
import {
  Box,
  IconButton,
  Chip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  InsertDriveFile as InsertDriveFileIcon,
} from '@mui/icons-material';

// File validation constants (same as ComposeBox)
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MB in bytes
const ALLOWED_EXTENSIONS = [
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv', '.txt', '.rtf',
  '.html', '.ics', '.jpg', '.jpeg', '.png', '.gif', '.heic', '.webp',
  '.mp3', '.mp4', '.zip', '.rar', '.7z', '.pptx', '.ppt'
];

// File validation utility
const validateFile = (file: File): { valid: boolean; error?: string } => {
  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `File "${file.name}" exceeds the maximum size of 20 MB`
    };
  }

  // Check file extension
  const fileName = file.name.toLowerCase();
  const lastDotIndex = fileName.lastIndexOf('.');

  if (lastDotIndex === -1 || lastDotIndex === fileName.length - 1) {
    return {
      valid: false,
      error: `File "${file.name}" has no valid extension. Allowed types: ${ALLOWED_EXTENSIONS.join(', ')}`
    };
  }

  const fileExtension = fileName.substring(lastDotIndex);

  if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
    return {
      valid: false,
      error: `File type "${fileExtension}" is not allowed. Allowed types: ${ALLOWED_EXTENSIONS.join(', ')}`
    };
  }

  return { valid: true };
};

// Format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

export interface SelectedFile {
  file: File;
  filename: string;
  file_size: number;
  file_type: string;
}

interface MessageAttachmentInputProps {
  selectedFiles: SelectedFile[];
  onFilesChange: (files: SelectedFile[]) => void;
  onError?: (error: string) => void;
  disabled?: boolean;
  /** Called when files are selected; return false to block adding. Run after picker so file dialog has user activation. */
  onAttachClick?: () => Promise<boolean> | boolean;
}

const MessageAttachmentInput: React.FC<MessageAttachmentInputProps> = ({
  selectedFiles,
  onFilesChange,
  onError,
  disabled = false,
  onAttachClick,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    // Capture file list as array before clearing input; clearing can invalidate event.target.files in some browsers
    const rawFiles = event.target.files;
    const filesArray = rawFiles && rawFiles.length > 0 ? Array.from(rawFiles) : [];
    if (fileInputRef.current) fileInputRef.current.value = '';
    console.log('[MessageAttachmentInput] files selected', { count: filesArray.length, names: filesArray.map((f) => f.name) });
    if (filesArray.length === 0) return;

    // Run validation after files selected so file picker opened with user activation
    if (onAttachClick) {
      const canProceed = await onAttachClick();
      if (!canProceed) return;
    }

    const validFiles: SelectedFile[] = [];
    const invalidFiles: Array<{ name: string; error: string }> = [];

    filesArray.forEach(file => {
      const validation = validateFile(file);
      if (validation.valid) {
        validFiles.push({
          file,
          filename: file.name,
          file_size: file.size,
          file_type: file.type,
        });
      } else {
        invalidFiles.push({ name: file.name, error: validation.error || 'Invalid file' });
      }
    });

    if (invalidFiles.length > 0 && onError) {
      invalidFiles.forEach(({ name, error }) => {
        onError(`${name}: ${error}`);
      });
    }

    if (validFiles.length > 0) {
      const nextFiles = [...selectedFiles, ...validFiles];
      console.log('[MessageAttachmentInput] calling onFilesChange', { validCount: validFiles.length, totalCount: nextFiles.length });
      onFilesChange(nextFiles);
    }
  };

  // Open file picker synchronously so browser allows it (user activation required)
  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = selectedFiles.filter((_, i) => i !== index);
    onFilesChange(updatedFiles);
  };

  const fileDisplay = selectedFiles.length > 0 && (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', minWidth: 0 }}>
      {selectedFiles.map((file, index) => (
        <Chip
          key={index}
          icon={
            <InsertDriveFileIcon
              sx={{ fontSize: 16, flexShrink: 0, color: theme.palette.text.secondary }}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, maxWidth: 200 }}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.75rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: 'text.primary',
                }}
                title={file.filename}
              >
                {file.filename}
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontSize: '0.7rem', color: 'text.secondary', flexShrink: 0 }}
              >
                ({formatFileSize(file.file_size)})
              </Typography>
            </Box>
          }
          onDelete={() => handleRemoveFile(index)}
          sx={{
            // grey[100] is always light in MUI — use dark surface in dark mode
            backgroundColor: isDark ? theme.palette.grey[800] : theme.palette.grey[100],
            border: `1px solid ${theme.palette.divider}`,
            color: theme.palette.text.primary,
            maxWidth: '100%',
            '& .MuiChip-deleteIcon': {
              color: theme.palette.text.secondary,
              '&:hover': {
                color: theme.palette.error.main,
              },
            },
          }}
        />
      ))}
    </Box>
  );

  return (
    <>
      <input
        type="file"
        multiple
        ref={fileInputRef}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
        <IconButton
          size="small"
          onClick={handleAttachClick}
          disabled={disabled}
          sx={{
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
              color: theme.palette.text.primary
            },
            '&:disabled': {
              color: theme.palette.action.disabled
            }
          }}
        >
          <AttachFileIcon fontSize="small" />
        </IconButton>
        {fileDisplay}
      </Box>
    </>
  );
};

export default MessageAttachmentInput;
